/**
 * API Configuration
 * 
 * API configuration for the WMS frontend including:
 * - Base URL configuration (supports window runtime, CRA, Vite)
 * - Service URLs mapping
 * - API endpoints definition
 * - Authentication endpoints
 * - Resource endpoints (users, orders, inventory, warehouses, etc.)
 * 
 * @module config/api.config
 */

// Base API URL: priority to window runtime, then CRA, then fallback to current origin + /api
const windowUrlRaw = (typeof window !== 'undefined' && window.__API_URL__) || null;
const WINDOW_API_URL = (windowUrlRaw && windowUrlRaw !== '%REACT_APP_API_BASE_URL%') ? windowUrlRaw : null;

const CRA_API_URL = (typeof process !== 'undefined' && (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL)) || null;

// Use current window origin + /api as fallback to support nginx deployments
// The Go workstation agent substitutes %REACT_APP_API_BASE_URL% with the full API URL including /api
// For nginx, we need to append /api since the placeholder is not substituted
const CURRENT_ORIGIN = (typeof window !== 'undefined' && window.location && window.location.origin) || 'https://xms-cloud.com';
const ORIGIN_WITH_API = CURRENT_ORIGIN + '/api';

const RAW_API_URL = WINDOW_API_URL || CRA_API_URL || ORIGIN_WITH_API;
export const API_BASE_URL = RAW_API_URL;

export const API_EXTENDED_URL = API_BASE_URL;

export const SERVICE_URLS = {
  INVENTORY: API_BASE_URL,
  ORDERS: API_BASE_URL,
  SHIPPING: API_BASE_URL,
  BILLING: API_BASE_URL,
  PLANNING: API_BASE_URL,
  ANALYTICS: API_BASE_URL,
  AUTH: API_BASE_URL,
  WAREHOUSES: API_BASE_URL,
  BLOCKCHAIN: API_BASE_URL,
  AI: API_BASE_URL,
  AI_TOOLS: API_BASE_URL,
  SAV: API_BASE_URL
};

// Endpoint configuration
export const API_ENDPOINTS = {
  // Authentication via API Gateway
  AUTH: '/api/v1/auth',
  LOGIN: '/api/v1/auth/login',
  LOGOUT: '/api/v1/auth/logout',
  REFRESH: '/api/v1/auth/refresh',
  PROFILE: '/api/v1/auth/me',

  // User management
  USERS: '/api/v1/users',
  ROLES: '/api/v1/roles',
  PERMISSIONS: '/api/v1/permissions',

  WAREHOUSES: '/api/v1/warehouses',
  LOCATIONS: '/api/v1/locations',

  INVENTORY: '/api/v1/inventory',
  INVENTORY_STATS: '/api/v1/inventory/stats',
  PRODUCTS: '/api/v1/products',
  STOCK_MOVEMENTS: '/api/v1/stock-movements',

  ORDERS: '/api/v1/orders',
  ORDER_ITEMS: '/api/v1/order-items',
  ORDER_STATS: '/api/v1/orders/stats',

  SHIPPING: '/api/v1/shipping',
  SHIPPING_STATS: '/api/v1/shipping/stats',

  BILLING: '/api/v1/billing',
  BILLING_STATS: '/api/v1/billing/stats/summary',

  PLANNING: '/api/v1/planning',
  PLANNING_STATS: '/api/v1/planning/stats',
  SHIPMENTS: '/api/v1/shipments',

  // Warehouse operations
  PICKING: '/api/v1/operations/picking',
  PICKING_WAVES: '/api/v1/operations/picking/waves',
  PICKING_LISTS: '/api/v1/operations/picking/lists',
  PACKING: '/api/v1/packing',
  RECEIVING: '/api/v1/receiving',
  ORDER_BATCHING: '/api/v1/order-batching',
  LOGISTICS_CARRIERS: '/api/v1/logistics-carriers',
  QUALITY: '/api/v1/quality',

  // Returns and after-sales service
  RETURNS: '/api/v1/returns',
  RETURN_ITEMS: '/api/v1/return-items',
  SAV: '/api/v1/sav',

  // Monitoring and reports
  MONITORING: '/api/v1/monitoring',
  REPORTS: '/api/v1/reports',
  ANALYTICS: '/api/v1/analytics',
  DASHBOARD: '/api/v1/dashboard',
  ALERTS: '/api/v1/alerts',
  SECURITY: '/api/v1/security',
  CANVAS: '/api/v1/canvas',

  SUPPORT: '/api/v1/support',

  // Specialized services
  FEEDBACK: '/api/v1/feedback',
  HR: '/api/v1/hr-management',
  SCHEDULING: '/api/v1/scheduling',
  MAINTENANCE: '/api/v1/maintenance',
  OPERATIONS: '/api/v1/operations',
  TRANSPORT: '/api/v1/transport',
  CONFIGURATIONS: '/api/v1/configurations',
  OPTIMIZATION: '/api/v1/hybrid-optimization',
  AI: '/api/v1/ai',
  INTEGRATIONS: '/api/v1/integrations',
  BLOCKCHAIN: '/api/v1/blockchain',
  EMOTIONAL: '/api/v1/emotional-intelligence',
  ADVANCED_AI: '/api/v1/advanced-ai',
  FORECASTING: '/api/v1/forecasting',
  INTERFACE: '/api/v1/interface',
  Q4_2025: '/api/v1/q4-2025',

  // External integrations
  MARKETPLACES: '/api/v1/marketplaces',
  CARRIERS: '/api/v1/carriers',
  WEBHOOKS: '/api/v1/webhooks',
  CHATBOT: '/api/v1/chatbot',
  MARKETPLACE: '/api/v1/marketplace',
  PARTNERS: '/api/v1/partners',
  SUPPLIERS: '/api/v1/suppliers',
  CUSTOMERS: '/api/v1/customers',

  ADMIN: '/api/v1/admin',
  HYBRID: '/api/v1/hybrid',
  SANDBOX: '/api/v1/sandbox',
  MULTI_WAREHOUSE: '/api/v1/multi-warehouse',
  PICKING_ROUTES: '/api/v1/operations/picking/routes',
  NOTIFICATIONS: '/api/v1/notifications',
  STRATEGIC: '/api/v1/strategic',
  Q2_2026: '/api/v1/q2-2026',
  AUTOMATION: '/api/v1/automation',

  CACHE: '/api/v1/cache',
  BACKUP_CONFIGS: '/api/v1/backup-configs',
  BACKUP_JOBS: '/api/v1/backup-jobs',

  // Amazon FBA
  FBA: '/api/v1/fba',
  FBA_SHIPMENTS: '/api/v1/fba/shipments',
  FBA_INVENTORY: '/api/v1/fba/inventory',
  FBA_REPORTS: '/api/v1/fba/reports',
  DIGITAL_TWIN: '/api/v1/digital-twin',

  // Workstation monitoring
  WORKSTATIONS: '/api/v1/workstations',
  WORKSTATION_MONITORING: '/api/v1/workstations/monitoring',

  // SpacetimeDB Infrastructure Monitoring (24 phases)
  SPACETIMEDB: '/api/v1/monitoring',
  SPACETIMEDB_DASHBOARD: '/api/v1/monitoring/dashboard',
  SPACETIMEDB_QUERY_METRICS: '/api/v1/monitoring/query-metrics',
  SPACETIMEDB_COMMITLOG: '/api/v1/monitoring/commitlog',
  SPACETIMEDB_SUBSCRIPTIONS: '/api/v1/monitoring/subscriptions',
  SPACETIMEDB_CONNECTIONS: '/api/v1/monitoring/connections',
  SPACETIMEDB_CDC: '/api/v1/monitoring/cdc',
  SPACETIMEDB_SCHEMA: '/api/v1/monitoring/schema',
  SPACETIMEDB_REDUCERS: '/api/v1/monitoring/reducers',
  SPACETIMEDB_SCHEDULED_REDUCERS: '/api/v1/monitoring/scheduled-reducers',
  SPACETIMEDB_ENERGY_BUDGETS: '/api/v1/monitoring/energy-budgets',
  SPACETIMEDB_RLS: '/api/v1/monitoring/rls',
  SPACETIMEDB_QUERY_OPTIMIZER: '/api/v1/monitoring/query-optimizer',
  SPACETIMEDB_TRANSACTIONS: '/api/v1/monitoring/transactions',
  SPACETIMEDB_REPLICATION: '/api/v1/monitoring/replication',
  SPACETIMEDB_FEDERATION: '/api/v1/monitoring/federation',
  SPACETIMEDB_CACHE: '/api/v1/monitoring/cache',
  SPACETIMEDB_EVENT_SOURCING: '/api/v1/monitoring/event-sourcing',
  SPACETIMEDB_CQRS: '/api/v1/monitoring/command-bus',
  SPACETIMEDB_SAGAS: '/api/v1/monitoring/sagas',
  SPACETIMEDB_WORKFLOW: '/api/v1/monitoring/workflow',

  // Utility Pattern Monitoring (Fleetbase-inspired)
  UTILITY_MONITORING: '/api/v1/monitoring/utilities',
  UTILITY_CIRCUIT_BREAKERS: '/api/v1/monitoring/utilities/circuit-breakers',
  UTILITY_LOCKS: '/api/v1/monitoring/utilities/locks',
  UTILITY_STATE_MACHINES: '/api/v1/monitoring/utilities/state-machines',
  UTILITY_FEATURE_FLAGS: '/api/v1/monitoring/utilities/feature-flags',
  UTILITY_HEALTH_CHECKS: '/api/v1/monitoring/utilities/health-checks',
  UTILITY_METRICS: '/api/v1/monitoring/utilities/metrics',
  UTILITY_DASHBOARD: '/api/v1/monitoring/utilities/dashboard'
};

/**
 * Workstation Local Agent Configuration
 * 
 * The Go workstation agent runs locally on each workstation machine
 * and provides direct access to hardware (scanners, cameras, etc.)
 * 
 * Environment variable: REACT_APP_WORKSTATION_AGENT_URL
 * Default: http://localhost:8080 (Go agent default port)
 */
const WORKSTATION_AGENT_URL_RAW =
  (typeof window !== 'undefined' && window.__WORKSTATION_AGENT_URL__) ||
  (typeof process !== 'undefined' && process.env.REACT_APP_WORKSTATION_AGENT_URL) ||
  'http://localhost:8080';

export const WORKSTATION_AGENT_URL = WORKSTATION_AGENT_URL_RAW;

/**
 * Workstation AI Agent URL
 * 
 * The Python AI troubleshooting agent runs locally on each workstation
 * and provides AI-powered diagnostics, camera streaming, and workflow control.
 * 
 * Environment variable: REACT_APP_WORKSTATION_AI_AGENT_URL
 * Default: http://localhost:8765 (AI agent default port)
 */
const WORKSTATION_AI_AGENT_URL_RAW =
  (typeof window !== 'undefined' && window.__WORKSTATION_AI_AGENT_URL__) ||
  (typeof process !== 'undefined' && process.env.REACT_APP_WORKSTATION_AI_AGENT_URL) ||
  'http://localhost:8765';

export const WORKSTATION_AI_AGENT_URL = WORKSTATION_AI_AGENT_URL_RAW;

/**
 * Workstation Agent Endpoints (Go Agent - runs locally)
 * These endpoints communicate directly with the local Go agent
 */
export const WORKSTATION_AGENT_ENDPOINTS = {
  // Health and status
  HEALTH: '/health',
  INVENTORY: '/inventory',
  INVENTORY_REFRESH: '/inventory/refresh',
  METRICS: '/metrics',
  SELECTED: '/selected',
  SELECT: '/select',
  
  // Mesh network status
  MESH_STATUS: '/mesh/status',
  MESH_PEERS: '/mesh/peers',
  MESH_SYNC: '/mesh/sync',
  MESH_HEALTH: '/mesh/health',
  MESH_EVENTS: '/mesh/events',
  MESH_LEADER: '/mesh/leader',
  MESH_CONFIG: '/mesh/config',
  MESH_STATS: '/mesh/stats',
  MESH_TRIGGER_FIX: '/mesh/trigger-fix',

  // Device management
  DEVICES: '/devices',
  DEVICES_REFRESH: '/devices/refresh',
  DEVICES_SELECT: '/devices/select',

  // Scanner operations
  SCAN_LAST: '/scan/last',
  SCAN_SIMULATE: '/scan/simulate',
  SCAN_DEVICES: '/scan/devices',
  SCAN_SERIAL_LIST: '/scan/serial/list',
  SCAN_SERIAL_PORTS: '/scan/serial/ports',
  SCAN_SERIAL_PROBE: '/scan/serial/probe',
  SCAN_SERIAL_START: '/scan/serial/start',
  SCAN_SERIAL_STOP: '/scan/serial/stop',
  SCAN_SERIAL_STATUS: '/scan/serial/status',

  // Camera/OBSBOT control
  CAMERA_FRAME: '/camera/frame',
  CAMERA_STREAM: '/camera/stream',
  CAMERA_STREAM_START: '/camera/stream/start',
  CAMERA_STREAM_STOP: '/camera/stream/stop',
  CAMERA_STREAM_STATS: '/camera/stream/stats',
  CAMERA_WAKE: '/camera/wake',
  CAMERA_SLEEP: '/camera/sleep',
  CAMERA_ZOOM: '/camera/zoom',
  CAMERA_TRACKING: '/camera/tracking',

  // Camera requirements (USB priority)
  CAMERA_REQUIREMENTS: '/camera/requirements',
  CAMERA_REQUIREMENTS_STATUS: '/camera/requirements/status',
  CAMERA_REQUIREMENTS_ASSIGN: '/camera/requirements/assign',
  // Gimbal control
  GIMBAL_HOME: '/camera/gimbal/home',
  GIMBAL_POSITION: '/camera/gimbal/position',
  GIMBAL_SAVE_HOME: '/camera/gimbal/save-home',
  GIMBAL_RESET: '/camera/gimbal/reset',

  // OBSBOT management
  OBSBOT_CONNECT: '/obsbot/connect',
  OBSBOT_STATUS: '/obsbot/status',

  // OBSBOT Center (auto-centering)
  OBSBOT_CENTER_STATUS: '/obsbot/center/status',
  OBSBOT_CENTER_START: '/obsbot/center/start',

  // OBSBOT Test endpoints (diagnostics)
  OBSBOT_TEST_DIAGNOSE: '/obsbot/test/diagnose',
  OBSBOT_TEST_WAKE: '/obsbot/test/wake',
  OBSBOT_TEST_GIMBAL: '/obsbot/test/gimbal',
  OBSBOT_TEST_FRAME: '/obsbot/test/frame',

  // OBSBOT SDK (Python SDK control)
  OBSBOT_SDK_STATUS: '/obsbot/sdk/status',
  OBSBOT_SDK_MOVE: '/obsbot/sdk/move',
  OBSBOT_SDK_STOP: '/obsbot/sdk/stop',
  OBSBOT_SDK_RESET: '/obsbot/sdk/reset',
  OBSBOT_SDK_FRAME: '/obsbot/sdk/frame',

  // OBSBOT Native SDK (direct hardware control)
  OBSBOT_NATIVE_STATUS: '/obsbot/native/status',
  OBSBOT_NATIVE_MOVE: '/obsbot/native/move',
  OBSBOT_NATIVE_STOP: '/obsbot/native/stop',
  OBSBOT_NATIVE_RESET: '/obsbot/native/reset',
  OBSBOT_NATIVE_ZOOM: '/obsbot/native/zoom',
  OBSBOT_NATIVE_TRACKING: '/obsbot/native/tracking',
  OBSBOT_NATIVE_WAKE: '/obsbot/native/wake',
  OBSBOT_NATIVE_SAVE_BOOT_PRESET: '/obsbot/native/save-boot-preset',
  OBSBOT_NATIVE_POSITION: '/obsbot/native/position',

  // OBSBOT Calibration (position calibration)
  OBSBOT_CALIBRATION_START: '/obsbot/calibration/start',
  OBSBOT_CALIBRATION_STATUS: '/obsbot/calibration/status',
  OBSBOT_CALIBRATION_HOME: '/obsbot/calibration/home',
  OBSBOT_CALIBRATION_SAVE: '/obsbot/calibration/save',

  // Gimbal velocity control
  GIMBAL_VELOCITY: '/camera/gimbal/velocity',
  GIMBAL_STOP: '/camera/gimbal/stop',

  // Camera tracking (AI-based object tracking)
  TRACKING_START: '/camera/tracking/start',
  TRACKING_STOP: '/camera/tracking/stop',
  TRACKING_STATUS: '/camera/tracking/status',
  TRACKING_CONFIG: '/camera/tracking/config',
  TRACKING_DETECTIONS: '/camera/tracking/detections',

  // Local inference (Roboflow) - runs on workstation, NOT through server
  INFERENCE_CONFIG: '/inference/config',
  INFERENCE_DETECT: '/inference/detect',
  INFERENCE_STREAM: '/inference/stream',
  INFERENCE_LAST: '/inference/last',
  INFERENCE_UPLOAD: '/inference/upload',

  // Real-time events (SSE - replaces MQTT)
  EVENTS: '/events',
  EVENTS_STATS: '/events/stats',
  EVENTS_WEBHOOK: '/events/webhook',

  // MQTT (legacy, prefer SSE events)
  MQTT_STATUS: '/mqtt/status',
  MQTT_CONNECT: '/mqtt/connect',

  // Port discovery
  PORTS_DISCOVER: '/ports/discover',
  PORTS_FIND: '/ports/find',
  PORTS_LIST: '/ports/list',

  // Config management
  CONFIG_RELOAD: '/config/reload',
  CONFIG_STATUS: '/config/status',

  // Recording (scan-triggered video capture)
  RECORDING_START: '/camera/recording/start',
  RECORDING_STOP: '/camera/recording/stop',
  RECORDING_STATUS: '/camera/recording/status',
  RECORDING_LIST: '/camera/recording/list',
  RECORDING_GET: '/camera/recording/', // + {id}
  RECORDING_TRIGGERS: '/camera/recording/triggers',

  // Camera/device discovery (Bonjour/mDNS)
  DISCOVERY_SCAN: '/discovery/scan',
  DISCOVERY_CAMERAS: '/discovery/cameras',
  DISCOVERY_OBSBOT: '/discovery/obsbot',
  DISCOVERY_WORKSTATION: '/discovery/workstation',
  DISCOVERY_PRINTERS: '/discovery/printers',

  // Printer management
  PRINTERS_LIST: '/printers',
  PRINTERS_DEFAULT: '/printers/default',
  PRINTERS_PRINT: '/printers/print',
  PRINTERS_REFRESH: '/printers/refresh',

  // Printer requirements (label + A4)
  PRINTERS_REQUIREMENTS: '/printers/requirements',
  PRINTERS_REQUIREMENTS_STATUS: '/printers/requirements/status',
  PRINTERS_REQUIREMENTS_ASSIGN: '/printers/requirements/assign',
  PRINTERS_LABEL: '/printers/label',
  PRINTERS_A4: '/printers/a4',
  
  // Backend shipping label printing (fetches label from backend and prints)
  PRINTER_BACKEND_SHIPPING_LABEL: '/printer/backend-shipping-label',
  
  // Backend config (fetch device configs from WMS backend)
  BACKEND_CONFIG: '/backend/config',
  BACKEND_CONFIG_PRINTERS: '/backend/config/printers',
  BACKEND_CONFIG_CAMERAS: '/backend/config/cameras',

  // Product counting (offline capable) - uses local YOLO inference
  COUNTING_COUNT: '/counting/count',
  COUNTING_CONFIG: '/counting/config',
  COUNTING_LAST: '/counting/last',
  COUNTING_TEST: '/counting/test',

  // Auto-training pipeline for product recognition
  TRAINING_STATUS: '/training/status',
  TRAINING_CONFIG: '/training/config',
  TRAINING_SAMPLE: '/training/sample',
  TRAINING_CORRECTION: '/training/correction',
  TRAINING_TRIGGER: '/training/trigger',
  TRAINING_SYNC: '/training/sync',
  TRAINING_SESSION_START: '/training/session/start',
  TRAINING_SESSION_END: '/training/session/end',
  TRAINING_SESSIONS: '/training/sessions',

  // Auto-update endpoints
  UPDATE_CHECK: '/admin/update/check',
  UPDATE_APPLY: '/admin/update/apply',
  UPDATE_BINARY: '/admin/update/binary',

  // LLM-assisted device identification
  IDENTIFY_DEVICE: '/identify',
  IDENTIFY_ALL: '/identify/all',
  IDENTIFY_CACHE: '/identify/cache',
  IDENTIFY_CONFIG: '/identify/config',

  // Startup / Kiosk mode / Firewall
  STARTUP_CONFIG: '/startup/config',
  STARTUP_KIOSK: '/startup/kiosk',
  STARTUP_AUTOSTART: '/startup/autostart',
  STARTUP_FIREWALL: '/startup/firewall',
  STARTUP_FIREWALL_FIX: '/startup/firewall/fix',

  // Workflow management (operator login/logout with face capture)
  WORKFLOW_LOGIN: '/workflow/login',
  WORKFLOW_LOGOUT: '/workflow/logout',
  WORKFLOW_STATUS: '/workflow/status',
  WORKFLOW_SCAN: '/workflow/scan',
  WORKFLOW_FACE: '/workflow/face',
  WORKFLOW_EVENTS: '/workflow/events',
  WORKFLOW_FEEDBACK: '/workflow/feedback',
  WORKFLOW_INIT: '/workflow/init',

  // Device testing
  TEST_ALL: '/test/all',
  TEST_CAMERA: '/test/camera',
  TEST_PRINTER: '/test/printer',
  TEST_SCANNER: '/test/scanner',
  TEST_RFID: '/test/rfid',

  // Remote administration (admin/root/sudo with OS detection)
  ADMIN_SYSTEM: '/admin/system',
  ADMIN_EXEC: '/admin/exec',
  ADMIN_BASH: '/admin/bash',
  ADMIN_CMD: '/admin/cmd',
  ADMIN_POWERSHELL: '/admin/powershell',
  ADMIN_SERVICE: '/admin/service',
  ADMIN_PACKAGE: '/admin/package',
  ADMIN_PROCESS: '/admin/process',
  ADMIN_REBOOT: '/admin/reboot',
  ADMIN_REGISTER: '/admin/register',
  ADMIN_TAILSCALE: '/admin/tailscale',

  // Mobile proxy (PDA/handheld device support via mesh gateway)
  MOBILE_LOGIN: '/mobile/login',
  MOBILE_PICK: '/mobile/pick',
  MOBILE_TASK: '/mobile/task',

  // Axis network camera endpoints (VAPIX API integration)
  AXIS_DISCOVER: '/axis/discover',
  AXIS_CAMERAS: '/axis/cameras',
  AXIS_CONNECT: '/axis/connect',
  AXIS_SNAPSHOT: '/axis/snapshot',
  AXIS_STREAM: '/axis/stream',
  AXIS_PTZ_MOVE: '/axis/ptz/move',
  AXIS_PTZ_HOME: '/axis/ptz/home',
  AXIS_PTZ_POSITION: '/axis/ptz/position',

  // Resilience/Circuit breaker endpoints
  RESILIENCE_STATUS: '/resilience/status',
  RESILIENCE_STATS: '/resilience/stats',
  RESILIENCE_BREAKERS: '/resilience/breakers',
  RESILIENCE_RESET: '/resilience/reset',
  RESILIENCE_CREATE: '/resilience/create',

  // Lifecycle/Shutdown management endpoints
  LIFECYCLE_STATUS: '/lifecycle/status',
  LIFECYCLE_SHUTDOWN: '/lifecycle/shutdown',
  LIFECYCLE_REGISTER: '/lifecycle/register',

  // Logging endpoints (structured logging with server integration)
  LOGS_STATUS: '/logs/status',
  LOGS_ENTRIES: '/logs/entries',
  LOGS_LEVEL: '/logs/level',
  LOGS_FLUSH: '/logs/flush',
  LOGS_WRITE: '/logs/write',

  // Device testing endpoints (comprehensive test suite)
  TESTING_REPORT: '/testing/report',
  TESTING_RUN: '/testing/run',
  TESTING_STATUS: '/testing/status',

  // Health check endpoints (K8s compatible liveness/readiness probes)
  HEALTH_LIVE: '/health/live',
  HEALTH_READY: '/health/ready',
  HEALTH_COMPONENTS: '/health/components',

  // Config watcher endpoints (hot-reload support)
  CONFIG_WATCH_STATUS: '/config/watch/status',
  CONFIG_WATCH_RELOAD: '/config/watch/reload',

  // LLM device identification endpoints
  IDENTIFY_DEVICE_2: '/identify/device',
  IDENTIFY_BATCH: '/identify/batch',
  IDENTIFY_CACHE_2: '/identify/cache',
  IDENTIFY_CACHE_CLEAR: '/identify/cache/clear',

  // Metrics management endpoints
  METRICS_SNAPSHOT: '/metrics/snapshot',
  METRICS_RESET: '/metrics/reset',

  // Startup management endpoints (kiosk, autostart, firewall)
  STARTUP_CONFIG_2: '/startup/config',
  STARTUP_UPDATE_CONFIG: '/startup/config/update',
  KIOSK_LAUNCH: '/kiosk/launch',
  KIOSK_STOP: '/kiosk/stop',
  AUTOSTART_REGISTER: '/autostart/register',
  AUTOSTART_UNREGISTER: '/autostart/unregister',
  AUTOSTART_STATUS: '/autostart/status',
  FIREWALL_CHECK: '/firewall/check',
  FIREWALL_ADD_RULE: '/firewall/rule/add',

  // Remote admin endpoints (system control, command execution)
  REMOTE_ADMIN_SYSTEM_INFO: '/admin/system/info',
  REMOTE_ADMIN_EXECUTE: '/admin/execute',
  REMOTE_ADMIN_POWERSHELL: '/admin/powershell',
  REMOTE_ADMIN_BASH: '/admin/bash',
  REMOTE_ADMIN_REBOOT: '/admin/reboot',
  REMOTE_ADMIN_SHUTDOWN: '/admin/shutdown',
  REMOTE_ADMIN_RESTART_AGENT: '/admin/restart-agent',
  REMOTE_ADMIN_TAILSCALE_IP: '/admin/tailscale-ip',
  REMOTE_ADMIN_SERVICE_MANAGE: '/admin/service/manage',
  REMOTE_ADMIN_DISK_USAGE: '/admin/disk/usage',
  REMOTE_ADMIN_MEMORY_USAGE: '/admin/memory/usage',
  REMOTE_ADMIN_PROCESS_LIST: '/admin/process/list',
  REMOTE_ADMIN_KILL_PROCESS: '/admin/process/kill',

  // mDNS discovery endpoints (network device discovery)
  MDNS_DISCOVER_PRINTERS: '/mdns/discover/printers',
  MDNS_DISCOVER_CAMERAS: '/mdns/discover/cameras',
  MDNS_DISCOVER_ALL: '/mdns/discover/all',

  // Printer Manager endpoints (comprehensive printer management)
  PRINTER_MANAGER_DISCOVER: '/printer/manager/discover',
  PRINTER_MANAGER_LIST: '/printer/manager/list',
  PRINTER_MANAGER_GET: '/printer/manager/get',
  PRINTER_MANAGER_DEFAULT: '/printer/manager/default',
  PRINTER_MANAGER_WORKSTATION: '/printer/manager/workstation',
  PRINTER_MANAGER_BY_TYPE: '/printer/manager/by-type',
  PRINTER_MANAGER_PRINT: '/printer/manager/print',

  // Workflow Orchestrator endpoints (workflow state management)
  WORKFLOW_ORCHESTRATOR_STATUS: '/workflow/orchestrator/status',
  WORKFLOW_ORCHESTRATOR_STATE: '/workflow/orchestrator/state',
  WORKFLOW_ORCHESTRATOR_EVENTS: '/workflow/orchestrator/events',
  WORKFLOW_ORCHESTRATOR_RESET: '/workflow/orchestrator/reset',
  WORKFLOW_ORCHESTRATOR_FACE_CAPTURE: '/workflow/orchestrator/face-capture',

  // Device Manager endpoints (device inventory and selection)
  DEVICE_MANAGER_INVENTORY: '/device/manager/inventory',
  DEVICE_MANAGER_REFRESH: '/device/manager/refresh',
  DEVICE_MANAGER_SELECT: '/device/manager/select',

  // Offline Buffer endpoints (backend connectivity and queue)
  OFFLINE_BUFFER_STATUS: '/offline/buffer/status',
  OFFLINE_BUFFER_QUEUE_SIZE: '/offline/buffer/queue-size',
  OFFLINE_BUFFER_BACKEND_STATUS: '/offline/buffer/backend-status',

  // Tiered Inference endpoints (counting/recognition modes)
  TIERED_INFERENCE_STATUS: '/tiered/inference/status',
  TIERED_INFERENCE_MODE: '/tiered/inference/mode',
  TIERED_INFERENCE_LAST_COUNT: '/tiered/inference/last-count',
  TIERED_INFERENCE_ANOMALIES: '/tiered/inference/anomalies',
  TIERED_INFERENCE_START: '/tiered/inference/start',
  TIERED_INFERENCE_STOP: '/tiered/inference/stop',
  TIERED_INFERENCE_SET_EXPECTATIONS: '/tiered/inference/set-expectations',

  // Auto-Learn endpoints (feedback and learning data)
  AUTO_LEARN_STATUS: '/autolearn/status',
  AUTO_LEARN_PENDING_REQUESTS: '/autolearn/pending-requests',
  AUTO_LEARN_LEARNING_DATA: '/autolearn/learning-data',
  AUTO_LEARN_SUBMIT_FEEDBACK: '/autolearn/submit-feedback',
  AUTO_LEARN_FLUSH: '/autolearn/flush',
  AUTO_LEARN_RESET: '/autolearn/reset',

  // Recording Management endpoints (video recording control)
  RECORDING_DELETE: '/recording/delete',
  RECORDING_SET_TRIGGERS: '/recording/set-triggers',
  RECORDING_CONFIG: '/recording/config',
  RECORDING_UPLOAD: '/recording/upload',

  // Event Broadcaster endpoints (SSE subscription management)
  BROADCASTER_STATUS: '/broadcaster/status',
  BROADCASTER_STATS: '/broadcaster/stats',
  BROADCASTER_PUBLISH: '/broadcaster/publish',

  // Gimbal Monitor endpoints (tamper detection)
  GIMBAL_MONITOR_STATUS: '/gimbal/monitor/status',
  GIMBAL_MONITOR_START: '/gimbal/monitor/start',
  GIMBAL_MONITOR_STOP: '/gimbal/monitor/stop',
  GIMBAL_MONITOR_SET_TARGET: '/gimbal/monitor/set-target',
  GIMBAL_MONITOR_CONFIG: '/gimbal/monitor/config',

  // Event Queue endpoints (offline queue management)
  EVENT_QUEUE_STATUS: '/event/queue/status',
  EVENT_QUEUE_STATS: '/event/queue/stats',
  EVENT_QUEUE_FLUSH: '/event/queue/flush',
  EVENT_QUEUE_CLEAR: '/event/queue/clear',

  // HTTP Uplink endpoints (backend connectivity)
  UPLINK_STATUS: '/uplink/status',
  UPLINK_STATS: '/uplink/stats',
  UPLINK_HEALTH_CHECK: '/uplink/health-check',

  // Gimbal Calibration endpoints (vision-based calibration)
  CALIBRATION_STATUS: '/calibration/status',
  CALIBRATION_START: '/calibration/start',
  CALIBRATION_RESULT: '/calibration/result',
  CALIBRATION_CONFIG: '/calibration/config',
  CALIBRATION_HOME: '/calibration/home',
  CALIBRATION_GO_TO_LIMIT: '/calibration/go-to-limit',

  // Camera Capture endpoints (video frame capture management)
  CAPTURE_OPEN: '/capture/open',
  CAPTURE_CLOSE: '/capture/close',
  CAPTURE_START: '/capture/start',
  CAPTURE_STOP: '/capture/stop',
  CAPTURE_STATUS: '/capture/status',
  CAPTURE_STATS: '/capture/stats',
  CAPTURE_FRAME: '/capture/frame',

  // MJPEG Stream endpoints (real-time video streaming)
  STREAM_MJPEG_START: '/stream/mjpeg/start',
  STREAM_MJPEG_STOP: '/stream/mjpeg/stop',
  STREAM_MJPEG_STATUS: '/stream/mjpeg/status',
  STREAM_MJPEG_STATS: '/stream/mjpeg/stats',

  // Bonjour Discovery endpoints (network device discovery)
  BONJOUR_START: '/bonjour/start',
  BONJOUR_STOP: '/bonjour/stop',
  BONJOUR_STATUS: '/bonjour/status',
  BONJOUR_DEVICES: '/bonjour/devices',
  BONJOUR_CAMERAS: '/bonjour/cameras',
  BONJOUR_OBSBOT: '/bonjour/obsbot',
  BONJOUR_WORKSTATION: '/bonjour/workstation',
  BONJOUR_DISCOVER_ONCE: '/bonjour/discover-once',

  // MQTT Client endpoints (message broker connectivity)
  MQTT_CLIENT_CONNECT: '/mqtt/connect',
  MQTT_CLIENT_DISCONNECT: '/mqtt/disconnect',
  MQTT_CLIENT_STATUS: '/mqtt/status',
  MQTT_PUBLISH_SCAN: '/mqtt/publish/scan',
  MQTT_PUBLISH_SYSTEM_INFO: '/mqtt/publish/system-info',
  MQTT_RECONNECT_START: '/mqtt/reconnect/start',
  MQTT_RECONNECT_STOP: '/mqtt/reconnect/stop',

  // Scanner Service endpoints (HID scanner management)
  SCANNER_START: '/scanner/start',
  SCANNER_STOP: '/scanner/stop',
  SCANNER_STATUS: '/scanner/status',
  SCANNER_LAST_SCAN: '/scanner/last-scan',
  SCANNER_SIMULATE: '/scanner/simulate',
  SCANNER_DEVICES: '/scanner/devices',
  SCANNER_PROCESS_INPUT: '/scanner/process-input',

  // Product Counter endpoints (offline counting configuration)
  COUNTER_CONFIG: '/counter/config',
  COUNTER_UPDATE_CONFIG: '/counter/update-config',
  COUNTER_INFERENCE_CONFIG: '/counter/inference-config',
  COUNTER_UPDATE_INFERENCE_CONFIG: '/counter/update-inference-config',
  COUNTER_OFFLINE_CAPABLE: '/counter/offline-capable',
  COUNTER_TEST_OFFLINE: '/counter/test-offline',
  COUNTER_SETUP_LOCAL: '/counter/setup-local',
  COUNTER_SETUP_CLOUD: '/counter/setup-cloud',

  // Roboflow Inference endpoints (AI inference management)
  ROBOFLOW_CONFIG: '/roboflow/config',
  ROBOFLOW_UPDATE_CONFIG: '/roboflow/update-config',
  ROBOFLOW_INFER: '/roboflow/infer',
  ROBOFLOW_LAST_FRAME: '/roboflow/last-frame',
  ROBOFLOW_OVERLAY: '/roboflow/overlay',
  ROBOFLOW_UPLOAD: '/roboflow/upload',

  // Device Classifier endpoints (device identification)
  CLASSIFIER_CLASSIFY: '/classifier/classify',
  CLASSIFIER_LIBRARY: '/classifier/library',
  CLASSIFIER_VENDORS: '/classifier/vendors',

  // Training Pipeline endpoints (extended training management)
  TRAINING_PIPELINE_START: '/training/pipeline/start',
  TRAINING_PIPELINE_STOP: '/training/pipeline/stop',
  TRAINING_PIPELINE_STATS: '/training/pipeline/stats',
  TRAINING_PIPELINE_UPLOAD: '/training/pipeline/upload',
  TRAINING_PIPELINE_ADD_SAMPLE: '/training/pipeline/add-sample',
  TRAINING_PIPELINE_ADD_CORRECTION: '/training/pipeline/add-correction',

  // HTTP Uplink endpoints (extended uplink management)
  UPLINK_SEND_SCAN: '/uplink/send-scan',
  UPLINK_SEND_BATCH: '/uplink/send-batch',
  UPLINK_CONFIG: '/uplink/config',

  // Event Queue endpoints (extended queue management)
  QUEUE_ADD: '/queue/add',
  QUEUE_SIZE: '/queue/size',
  QUEUE_START: '/queue/start',
  QUEUE_STOP: '/queue/stop',

  // Device Enumerator endpoints (device enumeration)
  ENUMERATOR_CAMERAS: '/enumerator/cameras',
  ENUMERATOR_PRINTERS: '/enumerator/printers',
  ENUMERATOR_SCANNERS: '/enumerator/scanners',
  ENUMERATOR_RFID: '/enumerator/rfid',
  ENUMERATOR_SERIAL: '/enumerator/serial',
  ENUMERATOR_MACHINE_ID: '/enumerator/machine-id',
  ENUMERATOR_HOSTNAME: '/enumerator/hostname',

  // Port Discovery endpoints (VID/PID-based port lookup)
  PORT_FIND_VIDPID: '/ports/find/vidpid',
  PORT_FIND_SERIAL: '/ports/find/serial',
  PORT_FIND_VID: '/ports/find/vid',
  PORT_FIND_RFID: '/ports/find/rfid',
  PORT_FIND_BARCODE: '/ports/find/barcode',
  PORT_WATCHER_START: '/ports/watcher/start',
  PORT_WATCHER_STOP: '/ports/watcher/stop',

  // HID Scanner endpoints (USB HID scanner management)
  HID_DISCOVER: '/hid/discover',
  HID_DETECT_TYPE: '/hid/detect-type',

  // Recording endpoints (extended recording management)
  RECORDING_GET_2: '/recording/get',
  RECORDING_CURRENT: '/recording/current',
  RECORDING_STATE: '/recording/state',

  // Mesh Aggregator endpoints (backend mesh data collection)
  MESH_AGGREGATOR_STATUS: '/mesh/aggregator/status',
  MESH_AGGREGATOR_REGISTER: '/mesh/aggregator/register',
  MESH_AGGREGATOR_UNREGISTER: '/mesh/aggregator/unregister',
  MESH_AGGREGATOR_FORCE_POLL: '/mesh/aggregator/force-poll',
  MESH_AGGREGATOR_WORKSTATION: '/mesh/aggregator/workstation',
  MESH_AGGREGATOR_START: '/mesh/aggregator/start',
  MESH_AGGREGATOR_STOP: '/mesh/aggregator/stop',

  // Scanner Vendor endpoints (vendor identification)
  SCANNER_VENDORS: '/scanner/vendors',
  SCANNER_IS_BARCODE: '/scanner/is-barcode',
  SCANNER_IS_RFID: '/scanner/is-rfid',

  // System Info endpoints (agent information)
  METRICS_DETAILED: '/metrics/detailed',
  SYSTEM_INFO: '/api/v1/system/info',
  AGENT_VERSION: '/agent/version',

  // Mesh Store endpoints (local SQLite data operations)
  MESH_STORE_STATS: '/mesh/store/stats',
  MESH_STORE_PENDING_SYNC: '/mesh/store/pending-sync',
  MESH_STORE_MARK_SYNCED: '/mesh/store/mark-synced',
  MESH_STORE_APPLY_REMOTE: '/mesh/store/apply-remote',
  MESH_STORE_MAX_VERSION: '/mesh/store/max-version',
  MESH_STORE_CUSTOMERS: '/mesh/store/customers',
  MESH_STORE_CUSTOMER: '/mesh/store/customer',
  MESH_STORE_LOCATIONS: '/mesh/store/locations',
  MESH_STORE_LOCATION: '/mesh/store/location',
  MESH_STORE_PRODUCTS: '/mesh/store/products',
  MESH_STORE_PRODUCT: '/mesh/store/product',
  MESH_STORE_PRODUCT_BY_SKU: '/mesh/store/product-by-sku',
  MESH_STORE_PRODUCT_BY_BARCODE: '/mesh/store/product-by-barcode',
  MESH_STORE_ORDERS: '/mesh/store/orders',
  MESH_STORE_ORDER: '/mesh/store/order',
  MESH_STORE_PENDING_ORDERS: '/mesh/store/pending-orders',

  // Mesh Discovery endpoints (peer management)
  MESH_DISCOVERY_STATUS: '/mesh/discovery/status',
  MESH_DISCOVERY_PEERS: '/mesh/discovery/peers',
  MESH_DISCOVERY_ONLINE_PEERS: '/mesh/discovery/online-peers',
  MESH_DISCOVERY_PEER: '/mesh/discovery/peer',
  MESH_DISCOVERY_START: '/mesh/discovery/start',
  MESH_DISCOVERY_STOP: '/mesh/discovery/stop',

  // Transport Manager endpoints (event delivery stats)
  TRANSPORT_STATS: '/transport/stats',
  TRANSPORT_QUEUE_SIZE: '/transport/queue-size',
  TRANSPORT_FLUSH_QUEUE: '/transport/flush-queue',
  TRANSPORT_UPLINK_STATS: '/transport/uplink-stats',
  TRANSPORT_QUEUE_STATS: '/transport/queue-stats',
  TRANSPORT_START: '/transport/start',
  TRANSPORT_STOP: '/transport/stop',

  // Face Capture endpoints (operator face capture config)
  FACE_CAPTURE_CONFIG: '/face-capture/config',
  FACE_CAPTURE_UPDATE_CONFIG: '/face-capture/update-config',
  FACE_CAPTURE_CAPTURE: '/face-capture/capture',

  // Backend Config Fetcher endpoints (workstation config from backend)
  BACKEND_WORKSTATION_CONFIG: '/backend/workstation-config',
  BACKEND_PRINTER_CONFIG: '/backend/printer-config',
  BACKEND_CAMERA_CONFIG: '/backend/camera-config',
  BACKEND_SCANNER_CONFIG: '/backend/scanner-config',
  BACKEND_INVALIDATE_CACHE: '/backend/invalidate-cache',
  BACKEND_CACHED_CONFIG: '/backend/cached-config',
  BACKEND_SET_URL: '/backend/set-url',
  BACKEND_SET_API_KEY: '/backend/set-api-key',
  BACKEND_START_PERIODIC_FETCH: '/backend/start-periodic-fetch',

  // Health Checker endpoints (comprehensive health management)
  HEALTH_REGISTER_CHECK: '/health/register-check',
  HEALTH_RUN_CHECKS: '/health/run-checks',
  HEALTH_LAST_REPORT: '/health/last-report',
  HEALTH_LIVENESS: '/health/liveness',
  HEALTH_READINESS: '/health/readiness',

  // Remote Admin extended endpoints (system management)
  ADMIN_DISK_USAGE: '/admin/disk-usage',
  ADMIN_MEMORY_USAGE: '/admin/memory-usage',
  ADMIN_PROCESS_LIST: '/admin/process-list',
  ADMIN_KILL_PROCESS: '/admin/kill-process',
  ADMIN_UPDATE_SYSTEM: '/admin/update-system',
  ADMIN_INSTALL_PACKAGE: '/admin/install-package',
  ADMIN_MANAGE_SERVICE: '/admin/manage-service',
  ADMIN_RESTART_AGENT: '/admin/restart-agent',
  ADMIN_SHUTDOWN: '/admin/shutdown',

  // MQTT Client extended endpoints (connection management)
  MQTT_START_RECONNECT: '/mqtt/start-reconnect',
  MQTT_STOP_RECONNECT: '/mqtt/stop-reconnect',
  MQTT_BROKER_INFO: '/mqtt/broker-info',
  MQTT_STATS: '/mqtt/stats',

  // Performance monitoring endpoints
  PERFORMANCE_STATS: '/api/v1/performance/stats',
  PERFORMANCE_ALERTS: '/api/v1/performance/alerts',
  PERFORMANCE_HISTORY: '/api/v1/performance/history',

  // Backup and recovery endpoints
  BACKUP_STATUS: '/backup/status',
  BACKUP_HISTORY: '/backup/history',
  BACKUP_CREATE: '/backup/create',
  BACKUP_RESTORE: '/backup/restore',
  BACKUP_DELETE: '/backup/delete',
  BACKUP_DOWNLOAD: '/backup/download',
  BACKUP_IMPORT: '/backup/import',
  BACKUP_SCHEDULE: '/backup/schedule',

  // AI Assistant endpoints (local chatbot for workstation troubleshooting)
  AI_HEALTH: '/ai/health',
  AI_CHAT: '/ai/chat',
  AI_IDENTIFY_DEVICE: '/ai/identify-device',
  AI_EXECUTE_COMMAND: '/ai/execute-command',
  AI_SUGGEST_COMMAND: '/ai/suggest-command',

  // Security endpoints (mesh network encryption and security management)
  SECURITY_STATUS: '/security/status',
  SECURITY_CONFIG: '/security/config',
  SECURITY_CONFIG_UPDATE: '/security/config/update',
  SECURITY_ENCRYPT: '/security/encrypt',
  SECURITY_DECRYPT: '/security/decrypt',
  SECURITY_KEYS_PUBLIC: '/security/keys/public',
  SECURITY_KEYS_REGISTER: '/security/keys/register',
  SECURITY_KEYS_PEERS: '/security/keys/peers',
  SECURITY_KEYS_ROTATE: '/security/keys/rotate',
  SECURITY_EVENTS: '/security/events',
  SECURITY_RATE_LIMIT_CHECK: '/security/rate-limit/check',
  SECURITY_SCAN: '/security/scan',

  // iDPRT SP-610 Pro thermal label printer endpoints (USB and Bluetooth SPP)
  IDPRT_DISCOVER: '/idprt/discover',
  IDPRT_LIST: '/idprt/list',
  IDPRT_ADD: '/idprt/add',
  IDPRT_REMOVE: '/idprt/remove',
  IDPRT_CONNECT: '/idprt/connect',
  IDPRT_DISCONNECT: '/idprt/disconnect',
  IDPRT_STATUS: '/idprt/status',
  IDPRT_PRINT: '/idprt/print',
  IDPRT_PRINT_RAW: '/idprt/print-raw',
  IDPRT_PRINT_TEST: '/idprt/print-test',
  IDPRT_CALIBRATE: '/idprt/calibrate',
  IDPRT_SELF_TEST: '/idprt/self-test',
  IDPRT_FEED: '/idprt/feed',
  IDPRT_CONFIG: '/idprt/config',
  IDPRT_BLUETOOTH_CONNECT: '/idprt/bluetooth/connect',
  IDPRT_KEEPALIVE_START: '/idprt/keepalive/start',
  IDPRT_KEEPALIVE_STOP: '/idprt/keepalive/stop',
  IDPRT_KEEPALIVE_STATUS: '/idprt/keepalive/status',
  IDPRT_WAKE: '/idprt/wake',
  IDPRT_PREVENT_SLEEP: '/idprt/prevent-sleep',

  // Zebra ZPL thermal label printer endpoints (USB, Bluetooth, and Network)
  ZEBRA_DISCOVER: '/zebra/discover',
  ZEBRA_LIST: '/zebra/list',
  ZEBRA_ADD: '/zebra/add',
  ZEBRA_REMOVE: '/zebra/remove',
  ZEBRA_CONNECT: '/zebra/connect',
  ZEBRA_DISCONNECT: '/zebra/disconnect',
  ZEBRA_STATUS: '/zebra/status',
  ZEBRA_PRINT: '/zebra/print',
  ZEBRA_PRINT_RAW: '/zebra/print-raw',
  ZEBRA_TEST: '/zebra/test',
  ZEBRA_CALIBRATE: '/zebra/calibrate',
  ZEBRA_SELF_TEST: '/zebra/self-test',
  ZEBRA_FEED: '/zebra/feed',
  ZEBRA_CONFIG: '/zebra/config',
  ZEBRA_RESET: '/zebra/reset',
  ZEBRA_CANCEL: '/zebra/cancel',

  // Brother QL thermal label printer endpoints (USB and Network)
  BROTHER_DISCOVER: '/brother/discover',
  BROTHER_LIST: '/brother/list',
  BROTHER_ADD: '/brother/add',
  BROTHER_REMOVE: '/brother/remove',
  BROTHER_CONNECT: '/brother/connect',
  BROTHER_DISCONNECT: '/brother/disconnect',
  BROTHER_STATUS: '/brother/status',
  BROTHER_PRINT: '/brother/print',
  BROTHER_PRINT_RAW: '/brother/print-raw',
  BROTHER_TEST: '/brother/test',
  BROTHER_FEED: '/brother/feed',
  BROTHER_CONFIG: '/brother/config',
  BROTHER_CANCEL: '/brother/cancel',
  BROTHER_LABELS: '/brother/labels',

  // DYMO LabelWriter thermal printer endpoints (USB and Network)
  DYMO_DISCOVER: '/dymo/discover',
  DYMO_LIST: '/dymo/list',
  DYMO_ADD: '/dymo/add',
  DYMO_REMOVE: '/dymo/remove',
  DYMO_CONNECT: '/dymo/connect',
  DYMO_DISCONNECT: '/dymo/disconnect',
  DYMO_STATUS: '/dymo/status',
  DYMO_PRINT: '/dymo/print',
  DYMO_PRINT_RAW: '/dymo/print-raw',
  DYMO_TEST: '/dymo/test',
  DYMO_FEED: '/dymo/feed',
  DYMO_CONFIG: '/dymo/config',
  DYMO_CANCEL: '/dymo/cancel',
  DYMO_LABELS: '/dymo/labels',

  // Boot sequence and splash screen endpoints (Tetris-like animation with anomaly detection)
  BOOT_STATUS: '/boot/status',
  BOOT_START: '/boot/start',
  BOOT_STOP: '/boot/stop',
  BOOT_ANOMALIES: '/boot/anomalies',
  BOOT_ANOMALY_ACTION: '/boot/anomaly/action',
  BOOT_SPLASH: '/boot/splash',
  BOOT_PROGRESS: '/boot/progress',
  BOOT_CONFIG: '/boot/config',
  BOOT_DIAGNOSTICS: '/boot/diagnostics',

  // WebSocket real-time updates endpoints
  WS_CONNECT: '/ws',
  WS_STATS: '/ws/stats',

  // Auto-configuration endpoints for peripheral devices
  AUTOCONFIG_STATS: '/autoconfig/stats',
  AUTOCONFIG_DEVICES: '/autoconfig/devices',
  AUTOCONFIG_PROFILES: '/autoconfig/profiles',
  AUTOCONFIG_SCAN: '/autoconfig/scan',
  AUTOCONFIG_DEVICE: '/autoconfig/device',

  // Task Queue Management endpoints
  TASKQUEUE_SUBMIT: '/taskqueue/submit',
  TASKQUEUE_CANCEL: '/taskqueue/cancel',
  TASKQUEUE_STATUS: '/taskqueue/status',
  TASKQUEUE_HISTORY: '/taskqueue/history',
  TASKQUEUE_HISTORY_CLEAR: '/taskqueue/history/clear',
  TASKQUEUE_PENDING: '/taskqueue/pending',
  TASKQUEUE_RUNNING: '/taskqueue/running',
  TASKQUEUE_STATS: '/taskqueue/stats',
  TASKQUEUE_SCHEDULE: '/taskqueue/schedule',
  TASKQUEUE_UNSCHEDULE: '/taskqueue/unschedule',
  TASKQUEUE_SCHEDULED: '/taskqueue/scheduled',

  // Network Diagnostics endpoints
  NETDIAG_DNS: '/netdiag/dns',
  NETDIAG_TCP: '/netdiag/tcp',
  NETDIAG_TLS: '/netdiag/tls',
  NETDIAG_HTTP: '/netdiag/http',
  NETDIAG_LATENCY: '/netdiag/latency',
  NETDIAG_INTERFACES: '/netdiag/interfaces',
  NETDIAG_STATS: '/netdiag/stats',
  NETDIAG_COMPREHENSIVE: '/netdiag/comprehensive',
  NETDIAG_HISTORY: '/netdiag/history',
  NETDIAG_HISTORY_CLEAR: '/netdiag/history/clear',
  NETDIAG_BACKEND: '/netdiag/backend',

  // Health Monitor endpoints
  HEALTHMON_SERVICES: '/healthmon/services',
  HEALTHMON_SERVICE: '/healthmon/service',
  HEALTHMON_REGISTER: '/healthmon/register',
  HEALTHMON_UNREGISTER: '/healthmon/unregister',
  HEALTHMON_CHECK: '/healthmon/check',
  HEALTHMON_ALERTS: '/healthmon/alerts',
  HEALTHMON_ALERTS_ACTIVE: '/healthmon/alerts/active',
  HEALTHMON_ALERT_ACK: '/healthmon/alert/ack',
  HEALTHMON_ALERT_RESOLVE: '/healthmon/alert/resolve',
  HEALTHMON_ALERTS_CLEAR: '/healthmon/alerts/clear',
  HEALTHMON_RECOVERY: '/healthmon/recovery',
  HEALTHMON_STATS: '/healthmon/stats',
  HEALTHMON_CONFIG: '/healthmon/config',

  // Metrics Aggregator endpoints
  METRICSAGG_METRICS: '/metricsagg/metrics',
  METRICSAGG_METRIC: '/metricsagg/metric',
  METRICSAGG_REGISTER: '/metricsagg/register',
  METRICSAGG_RECORD: '/metricsagg/record',
  METRICSAGG_TIMESERIES: '/metricsagg/timeseries',
  METRICSAGG_AGGREGATED: '/metricsagg/aggregated',
  METRICSAGG_SNAPSHOT: '/metricsagg/snapshot',
  METRICSAGG_STATS: '/metricsagg/stats',
  METRICSAGG_CLEAR: '/metricsagg/clear',
  METRICSAGG_PROMETHEUS: '/metricsagg/prometheus',
  METRICSAGG_CONFIG: '/metricsagg/config',

  // Offline Mode Manager endpoints
  OFFLINE_STATUS: '/offline/status',
  OFFLINE_CONFIG: '/offline/config',
  OFFLINE_STATS: '/offline/stats',
  OFFLINE_QUEUE: '/offline/queue',
  OFFLINE_QUEUE_ADD: '/offline/queue/add',
  OFFLINE_QUEUE_REMOVE: '/offline/queue/remove',
  OFFLINE_QUEUE_CLEAR: '/offline/queue/clear',
  OFFLINE_CACHE: '/offline/cache',
  OFFLINE_CACHE_SET: '/offline/cache/set',
  OFFLINE_CACHE_GET: '/offline/cache/get',
  OFFLINE_CACHE_INVALIDATE: '/offline/cache/invalidate',
  OFFLINE_CACHE_CLEAR: '/offline/cache/clear',
  OFFLINE_SYNC: '/offline/sync',
  OFFLINE_SYNC_TRIGGER: '/offline/sync/trigger',
  OFFLINE_SYNC_SESSIONS: '/offline/sync/sessions',
  OFFLINE_CONFLICTS: '/offline/conflicts',
  OFFLINE_CONFLICTS_RESOLVE: '/offline/conflicts/resolve',
  OFFLINE_CONNECTIVITY: '/offline/connectivity',
  OFFLINE_FORCE_ONLINE: '/offline/force/online',
  OFFLINE_FORCE_OFFLINE: '/offline/force/offline',

  // Enhanced Mesh Network endpoints
  MESHNET_STATUS: '/meshnet/status',
  MESHNET_CONFIG: '/meshnet/config',
  MESHNET_STATS: '/meshnet/stats',
  MESHNET_PEERS: '/meshnet/peers',
  MESHNET_PEER_ADD: '/meshnet/peer/add',
  MESHNET_PEER_REMOVE: '/meshnet/peer/remove',
  MESHNET_PEER: '/meshnet/peer',
  MESHNET_TOPOLOGY: '/meshnet/topology',
  MESHNET_ROUTES: '/meshnet/routes',
  MESHNET_DATA: '/meshnet/data',
  MESHNET_DATA_SET: '/meshnet/data/set',
  MESHNET_DATA_GET: '/meshnet/data/get',
  MESHNET_DATA_DELETE: '/meshnet/data/delete',
  MESHNET_ELECTION_START: '/meshnet/election/start',
  MESHNET_ELECTIONS: '/meshnet/elections',
  MESHNET_LEADER: '/meshnet/leader',
  MESHNET_MESSAGE_SEND: '/meshnet/message/send',
  MESHNET_MESSAGE_BROADCAST: '/meshnet/message/broadcast',
  MESHNET_PARTITIONS: '/meshnet/partitions',

  // Data Sync Engine endpoints
  SYNC_STATUS: '/sync/status',
  SYNC_CONFIG: '/sync/config',
  SYNC_STATS: '/sync/stats',
  SYNC_RECORDS: '/sync/records',
  SYNC_RECORDS_TYPE: '/sync/records/type',
  SYNC_RECORDS_DIRTY: '/sync/records/dirty',
  SYNC_RECORD_SET: '/sync/record/set',
  SYNC_RECORD_GET: '/sync/record/get',
  SYNC_RECORD_DELETE: '/sync/record/delete',
  SYNC_TRIGGER: '/sync/trigger',
  SYNC_PAUSE: '/sync/pause',
  SYNC_RESUME: '/sync/resume',
  SYNC_CONFLICTS: '/sync/conflicts',
  SYNC_CONFLICTS_PENDING: '/sync/conflicts/pending',
  SYNC_CONFLICT_RESOLVE: '/sync/conflict/resolve',
  SYNC_SESSIONS: '/sync/sessions',
  SYNC_SESSION_CURRENT: '/sync/session/current',
  SYNC_RULES: '/sync/rules',
  SYNC_RULE_ADD: '/sync/rule/add',
  SYNC_RULE_REMOVE: '/sync/rule/remove',
  SYNC_CLEAR_RECORDS: '/sync/clear/records',
  SYNC_CLEAR_CONFLICTS: '/sync/clear/conflicts',
  SYNC_RESET_STATS: '/sync/reset/stats',

  // Peer Communication Protocol endpoints
  PEERCOMM_STATUS: '/peercomm/status',
  PEERCOMM_CONFIG: '/peercomm/config',
  PEERCOMM_STATS: '/peercomm/stats',
  PEERCOMM_LOCAL: '/peercomm/local',
  PEERCOMM_PEERS: '/peercomm/peers',
  PEERCOMM_PEERS_CONNECTED: '/peercomm/peers/connected',
  PEERCOMM_PEER_ADD: '/peercomm/peer/add',
  PEERCOMM_PEER_REMOVE: '/peercomm/peer/remove',
  PEERCOMM_PEER_GET: '/peercomm/peer/get',
  PEERCOMM_PEER_PING: '/peercomm/peer/ping',
  PEERCOMM_SEND: '/peercomm/send',
  PEERCOMM_REQUEST: '/peercomm/request',
  PEERCOMM_BROADCAST: '/peercomm/broadcast',
  PEERCOMM_PUBLISH: '/peercomm/publish',
  PEERCOMM_SUBSCRIBE: '/peercomm/subscribe',
  PEERCOMM_UNSUBSCRIBE: '/peercomm/unsubscribe',
  PEERCOMM_CHANNELS: '/peercomm/channels',
  PEERCOMM_CHANNEL_CREATE: '/peercomm/channel/create',
  PEERCOMM_CHANNEL_DELETE: '/peercomm/channel/delete',
  PEERCOMM_SUBSCRIPTIONS: '/peercomm/subscriptions',
  PEERCOMM_HANDLER_REGISTER: '/peercomm/handler/register',
  PEERCOMM_HANDLER_UNREGISTER: '/peercomm/handler/unregister',
  PEERCOMM_PENDING: '/peercomm/pending',
  PEERCOMM_PENDING_CLEAR: '/peercomm/pending/clear',

  // Event Bus endpoints
  EVENTBUS_STATUS: '/eventbus/status',
  EVENTBUS_CONFIG: '/eventbus/config',
  EVENTBUS_STATS: '/eventbus/stats',
  EVENTBUS_START: '/eventbus/start',
  EVENTBUS_STOP: '/eventbus/stop',
  EVENTBUS_PUBLISH: '/eventbus/publish',
  EVENTBUS_SUBSCRIBE: '/eventbus/subscribe',
  EVENTBUS_UNSUBSCRIBE: '/eventbus/unsubscribe',
  EVENTBUS_SUBSCRIPTIONS: '/eventbus/subscriptions',
  EVENTBUS_SUBSCRIPTION_PAUSE: '/eventbus/subscription/pause',
  EVENTBUS_SUBSCRIPTION_RESUME: '/eventbus/subscription/resume',
  EVENTBUS_TOPICS: '/eventbus/topics',
  EVENTBUS_HISTORY: '/eventbus/history',
  EVENTBUS_HISTORY_TOPIC: '/eventbus/history/topic',
  EVENTBUS_HISTORY_TYPE: '/eventbus/history/type',
  EVENTBUS_HISTORY_CLEAR: '/eventbus/history/clear',
  EVENTBUS_RESET: '/eventbus/reset',

  // State Machine endpoints
  STATEMACHINE_STATUS: '/statemachine/status',
  STATEMACHINE_MACHINES: '/statemachine/machines',
  STATEMACHINE_MACHINE_CREATE: '/statemachine/machine/create',
  STATEMACHINE_MACHINE_DELETE: '/statemachine/machine/delete',
  STATEMACHINE_MACHINE_GET: '/statemachine/machine/',
  STATEMACHINE_MACHINE_START: '/statemachine/machine/start',
  STATEMACHINE_MACHINE_STOP: '/statemachine/machine/stop',
  STATEMACHINE_MACHINE_RESET: '/statemachine/machine/reset',
  STATEMACHINE_MACHINE_EVENT: '/statemachine/machine/event',
  STATEMACHINE_MACHINE_FORCE: '/statemachine/machine/force',
  STATEMACHINE_MACHINE_STATES: '/statemachine/machine/states',
  STATEMACHINE_MACHINE_STATE_ADD: '/statemachine/machine/state/add',
  STATEMACHINE_MACHINE_STATE_REMOVE: '/statemachine/machine/state/remove',
  STATEMACHINE_MACHINE_TRANSITIONS: '/statemachine/machine/transitions',
  STATEMACHINE_MACHINE_TRANSITION_ADD: '/statemachine/machine/transition/add',
  STATEMACHINE_MACHINE_TRANSITION_REMOVE: '/statemachine/machine/transition/remove',
  STATEMACHINE_MACHINE_HISTORY: '/statemachine/machine/history',
  STATEMACHINE_MACHINE_STATS: '/statemachine/machine/stats',
  STATEMACHINE_MACHINE_DATA: '/statemachine/machine/data',
  STATEMACHINE_MACHINE_DATA_SET: '/statemachine/machine/data/set',
  STATEMACHINE_MACHINE_DATA_CLEAR: '/statemachine/machine/data/clear',
  STATEMACHINE_MACHINE_EVENTS: '/statemachine/machine/events',

  // Plugin System endpoints
  PLUGIN_STATUS: '/plugin/status',
  PLUGIN_STATS: '/plugin/stats',
  PLUGIN_CONFIG: '/plugin/config',
  PLUGIN_CONFIG_UPDATE: '/plugin/config/update',
  PLUGIN_START: '/plugin/start',
  PLUGIN_STOP: '/plugin/stop',
  PLUGIN_PLUGINS: '/plugin/plugins',
  PLUGIN_PLUGINS_TYPE: '/plugin/plugins/type',
  PLUGIN_PLUGINS_STATE: '/plugin/plugins/state',
  PLUGIN_REGISTER: '/plugin/register',
  PLUGIN_UNREGISTER: '/plugin/unregister',
  PLUGIN_LOAD: '/plugin/load',
  PLUGIN_UNLOAD: '/plugin/unload',
  PLUGIN_PLUGIN_START: '/plugin/plugin/start',
  PLUGIN_PLUGIN_STOP: '/plugin/plugin/stop',
  PLUGIN_PLUGIN_RESTART: '/plugin/plugin/restart',
  PLUGIN_PLUGIN_ENABLE: '/plugin/plugin/enable',
  PLUGIN_PLUGIN_DISABLE: '/plugin/plugin/disable',
  PLUGIN_PLUGIN_GET: '/plugin/plugin/',
  PLUGIN_PLUGIN_CONFIG: '/plugin/plugin/config',
  PLUGIN_PLUGIN_CONFIG_UPDATE: '/plugin/plugin/config/update',
  PLUGIN_HOOKS: '/plugin/hooks',
  PLUGIN_HOOKS_PLUGIN: '/plugin/hooks/plugin',
  PLUGIN_HOOK_REGISTER: '/plugin/hook/register',
  PLUGIN_HOOK_UNREGISTER: '/plugin/hook/unregister',
  PLUGIN_STATS_RESET: '/plugin/stats/reset',

  // Configuration Manager endpoints
  CONFIGMGR_STATUS: '/configmgr/status',
  CONFIGMGR_STATS: '/configmgr/stats',
  CONFIGMGR_CONFIG: '/configmgr/config',
  CONFIGMGR_CONFIG_UPDATE: '/configmgr/config/update',
  CONFIGMGR_START: '/configmgr/start',
  CONFIGMGR_STOP: '/configmgr/stop',
  CONFIGMGR_SCHEMAS: '/configmgr/schemas',
  CONFIGMGR_SCHEMA_REGISTER: '/configmgr/schema/register',
  CONFIGMGR_SCHEMA_UNREGISTER: '/configmgr/schema/unregister',
  CONFIGMGR_SCHEMA_GET: '/configmgr/schema/',
  CONFIGMGR_VALUES: '/configmgr/values',
  CONFIGMGR_VALUES_TAG: '/configmgr/values/tag',
  CONFIGMGR_VALUES_SOURCE: '/configmgr/values/source',
  CONFIGMGR_VALUE_GET: '/configmgr/value/get',
  CONFIGMGR_VALUE_SET: '/configmgr/value/set',
  CONFIGMGR_VALUE_DELETE: '/configmgr/value/delete',
  CONFIGMGR_VALUE_RESET: '/configmgr/value/reset',
  CONFIGMGR_RESET: '/configmgr/reset',
  CONFIGMGR_PROFILES: '/configmgr/profiles',
  CONFIGMGR_PROFILE_CREATE: '/configmgr/profile/create',
  CONFIGMGR_PROFILE_DELETE: '/configmgr/profile/delete',
  CONFIGMGR_PROFILE_APPLY: '/configmgr/profile/apply',
  CONFIGMGR_PROFILE_GET: '/configmgr/profile/',
  CONFIGMGR_SNAPSHOTS: '/configmgr/snapshots',
  CONFIGMGR_SNAPSHOT_CREATE: '/configmgr/snapshot/create',
  CONFIGMGR_SNAPSHOT_RESTORE: '/configmgr/snapshot/restore',
  CONFIGMGR_SNAPSHOT_DELETE: '/configmgr/snapshot/delete',
  CONFIGMGR_SNAPSHOT_GET: '/configmgr/snapshot/',
  CONFIGMGR_HISTORY: '/configmgr/history',
  CONFIGMGR_HISTORY_CLEAR: '/configmgr/history/clear',
  CONFIGMGR_EXPORT: '/configmgr/export',
  CONFIGMGR_IMPORT: '/configmgr/import',
  CONFIGMGR_STATS_RESET: '/configmgr/stats/reset',

  // Resource Monitor endpoints
  RESOURCEMON_STATUS: '/resourcemon/status',
  RESOURCEMON_STATS: '/resourcemon/stats',
  RESOURCEMON_CONFIG: '/resourcemon/config',
  RESOURCEMON_CONFIG_UPDATE: '/resourcemon/config/update',
  RESOURCEMON_START: '/resourcemon/start',
  RESOURCEMON_STOP: '/resourcemon/stop',
  RESOURCEMON_CPU: '/resourcemon/cpu',
  RESOURCEMON_MEMORY: '/resourcemon/memory',
  RESOURCEMON_DISK: '/resourcemon/disk',
  RESOURCEMON_NETWORK: '/resourcemon/network',
  RESOURCEMON_GPU: '/resourcemon/gpu',
  RESOURCEMON_PROCESS: '/resourcemon/process',
  RESOURCEMON_HISTORY: '/resourcemon/history',
  RESOURCEMON_HISTORY_CLEAR: '/resourcemon/history/clear',
  RESOURCEMON_ALERTS: '/resourcemon/alerts',
  RESOURCEMON_ALERTS_ACKNOWLEDGE: '/resourcemon/alerts/acknowledge',
  RESOURCEMON_ALERTS_CLEAR: '/resourcemon/alerts/clear',
  RESOURCEMON_THRESHOLDS: '/resourcemon/thresholds',
  RESOURCEMON_THRESHOLD_SET: '/resourcemon/threshold/set',
  RESOURCEMON_THRESHOLD_REMOVE: '/resourcemon/threshold/remove',
  RESOURCEMON_SNAPSHOT: '/resourcemon/snapshot',
  RESOURCEMON_STATS_RESET: '/resourcemon/stats/reset',

  // Service Registry endpoints
  SERVICEREG_STATUS: '/servicereg/status',
  SERVICEREG_STATS: '/servicereg/stats',
  SERVICEREG_CONFIG: '/servicereg/config',
  SERVICEREG_CONFIG_UPDATE: '/servicereg/config/update',
  SERVICEREG_START: '/servicereg/start',
  SERVICEREG_STOP: '/servicereg/stop',
  SERVICEREG_SERVICES: '/servicereg/services',
  SERVICEREG_SERVICES_NAME: '/servicereg/services/name',
  SERVICEREG_SERVICES_TYPE: '/servicereg/services/type',
  SERVICEREG_SERVICES_STATUS: '/servicereg/services/status',
  SERVICEREG_SERVICES_TAGS: '/servicereg/services/tags',
  SERVICEREG_SERVICE_REGISTER: '/servicereg/service/register',
  SERVICEREG_SERVICE_DEREGISTER: '/servicereg/service/deregister',
  SERVICEREG_SERVICE_UPDATE: '/servicereg/service/update',
  SERVICEREG_SERVICE_HEARTBEAT: '/servicereg/service/heartbeat',
  SERVICEREG_SERVICE_GET: '/servicereg/service/',
  SERVICEREG_SERVICE_HEALTH: '/servicereg/service/health',
  SERVICEREG_SERVICE_HEALTH_CHECK: '/servicereg/service/health/check',
  SERVICEREG_DISCOVER: '/servicereg/discover',
  SERVICEREG_QUERY: '/servicereg/query',
  SERVICEREG_HEALTHY: '/servicereg/healthy',
  SERVICEREG_GROUPS: '/servicereg/groups',
  SERVICEREG_GROUP_CREATE: '/servicereg/group/create',
  SERVICEREG_GROUP_DELETE: '/servicereg/group/delete',
  SERVICEREG_GROUP_GET: '/servicereg/group/',
  SERVICEREG_GROUP_SERVICES: '/servicereg/group/services',
  SERVICEREG_GROUP_ADD: '/servicereg/group/add',
  SERVICEREG_GROUP_REMOVE: '/servicereg/group/remove',
  SERVICEREG_EXPORT: '/servicereg/export',
  SERVICEREG_IMPORT: '/servicereg/import',
  SERVICEREG_STATS_RESET: '/servicereg/stats/reset',

  // Mesh Discovery and Auto-Authentication endpoints
  MESHDISCOVERY_STATUS: '/meshdiscovery/status',
  MESHDISCOVERY_STATS: '/meshdiscovery/stats',
  MESHDISCOVERY_CONFIG: '/meshdiscovery/config',
  MESHDISCOVERY_CONFIG_UPDATE: '/meshdiscovery/config/update',
  MESHDISCOVERY_START: '/meshdiscovery/start',
  MESHDISCOVERY_STOP: '/meshdiscovery/stop',
  MESHDISCOVERY_PEERS: '/meshdiscovery/peers',
  MESHDISCOVERY_PEERS_AUTHENTICATED: '/meshdiscovery/peers/authenticated',
  MESHDISCOVERY_PEERS_ZONE: '/meshdiscovery/peers/zone',
  MESHDISCOVERY_PEER_GET: '/meshdiscovery/peer/',
  MESHDISCOVERY_PEER_PING: '/meshdiscovery/peer/ping',
  MESHDISCOVERY_PEER_TRUST: '/meshdiscovery/peer/trust',
  MESHDISCOVERY_PEER_REVOKE: '/meshdiscovery/peer/revoke',
  MESHDISCOVERY_PEER_REMOVE: '/meshdiscovery/peer/remove',
  MESHDISCOVERY_PEER_ADD: '/meshdiscovery/peer/add',
  MESHDISCOVERY_DISCOVER: '/meshdiscovery/discover',
  MESHDISCOVERY_ANNOUNCE: '/meshdiscovery/announce',
  MESHDISCOVERY_ZONES: '/meshdiscovery/zones',
  MESHDISCOVERY_PUBLICKEY: '/meshdiscovery/publickey',
  MESHDISCOVERY_TRUSTEDKEYS: '/meshdiscovery/trustedkeys',
  MESHDISCOVERY_EVENTS: '/meshdiscovery/events',
  MESHDISCOVERY_EXPORT: '/meshdiscovery/export',
  MESHDISCOVERY_IMPORT: '/meshdiscovery/import',
  MESHDISCOVERY_SECRET_SET: '/meshdiscovery/secret/set',
  MESHDISCOVERY_STATS_RESET: '/meshdiscovery/stats/reset',
  MESHDISCOVERY_ADDRESSES: '/meshdiscovery/addresses',

  // Mesh Balancer endpoints for load balancing and failover
  MESHBALANCER_STATUS: '/meshbalancer/status',
  MESHBALANCER_STATS: '/meshbalancer/stats',
  MESHBALANCER_CONFIG: '/meshbalancer/config',
  MESHBALANCER_CONFIG_UPDATE: '/meshbalancer/config/update',
  MESHBALANCER_START: '/meshbalancer/start',
  MESHBALANCER_STOP: '/meshbalancer/stop',
  MESHBALANCER_STRATEGY: '/meshbalancer/strategy',
  MESHBALANCER_STRATEGY_SET: '/meshbalancer/strategy/set',
  MESHBALANCER_PEERS: '/meshbalancer/peers',
  MESHBALANCER_PEERS_HEALTHY: '/meshbalancer/peers/healthy',
  MESHBALANCER_PEER_ADD: '/meshbalancer/peer/add',
  MESHBALANCER_PEER_REMOVE: '/meshbalancer/peer/remove',
  MESHBALANCER_PEER_WEIGHT: '/meshbalancer/peer/weight',
  MESHBALANCER_PEER_MAINTENANCE: '/meshbalancer/peer/maintenance',
  MESHBALANCER_PEER_CAPACITY: '/meshbalancer/peer/capacity',
  MESHBALANCER_HEALTH: '/meshbalancer/health',
  MESHBALANCER_HEALTH_ALL: '/meshbalancer/health/all',
  MESHBALANCER_BANDWIDTH: '/meshbalancer/bandwidth',
  MESHBALANCER_BANDWIDTH_ALL: '/meshbalancer/bandwidth/all',
  MESHBALANCER_BANDWIDTH_RECORD: '/meshbalancer/bandwidth/record',
  MESHBALANCER_SELECT: '/meshbalancer/select',
  MESHBALANCER_RELEASE: '/meshbalancer/release',
  MESHBALANCER_FAILOVER_EVENTS: '/meshbalancer/failover/events',
  MESHBALANCER_SESSIONS: '/meshbalancer/sessions',

  // Network Interface endpoints for cross-platform interface detection
  NETINTERFACE_STATUS: '/netinterface/status',
  NETINTERFACE_INTERFACES: '/netinterface/interfaces',
  NETINTERFACE_INTERFACES_ACTIVE: '/netinterface/interfaces/active',
  NETINTERFACE_INTERFACES_VPN: '/netinterface/interfaces/vpn',
  NETINTERFACE_INTERFACES_TAILSCALE: '/netinterface/interfaces/tailscale',
  NETINTERFACE_INTERFACES_LOCAL: '/netinterface/interfaces/local',
  NETINTERFACE_INTERFACE: '/netinterface/interface',
  NETINTERFACE_INTERFACE_TYPE: '/netinterface/interface/type',
  NETINTERFACE_STATS: '/netinterface/stats',
  NETINTERFACE_CONFIG: '/netinterface/config',
  NETINTERFACE_CONFIG_UPDATE: '/netinterface/config/update',
  NETINTERFACE_START: '/netinterface/start',
  NETINTERFACE_STOP: '/netinterface/stop',
  NETINTERFACE_RESCAN: '/netinterface/rescan',
  NETINTERFACE_BEST: '/netinterface/best',
  NETINTERFACE_BEST_VPN: '/netinterface/best/vpn',
  NETINTERFACE_DISCOVERY_ADDRESSES: '/netinterface/discovery/addresses',
  NETINTERFACE_BROADCAST_ADDRESSES: '/netinterface/broadcast/addresses',
  NETINTERFACE_TAILSCALE_STATUS: '/netinterface/tailscale/status',
  NETINTERFACE_TAILSCALE_PEERS: '/netinterface/tailscale/peers',
  NETINTERFACE_TAILSCALE_PEERS_ONLINE: '/netinterface/tailscale/peers/online',
  NETINTERFACE_CONNECTIVITY: '/netinterface/connectivity',
  NETINTERFACE_GATEWAY: '/netinterface/gateway',
  NETINTERFACE_DNS: '/netinterface/dns',
  NETINTERFACE_TYPES: '/netinterface/types',
  NETINTERFACE_PROVIDERS: '/netinterface/providers',
  NETINTERFACE_FILTER: '/netinterface/filter',
  NETINTERFACE_EVENTS: '/netinterface/events',
  NETINTERFACE_EXPORT: '/netinterface/export',

  // Mesh Integration endpoints for multi-interface mesh discovery
  MESHINTEGRATION_STATUS: '/meshintegration/status',
  MESHINTEGRATION_STATS: '/meshintegration/stats',
  MESHINTEGRATION_CONFIG: '/meshintegration/config',
  MESHINTEGRATION_CONFIG_UPDATE: '/meshintegration/config/update',
  MESHINTEGRATION_START: '/meshintegration/start',
  MESHINTEGRATION_STOP: '/meshintegration/stop',
  MESHINTEGRATION_INTERFACES: '/meshintegration/interfaces',
  MESHINTEGRATION_INTERFACES_ACTIVE: '/meshintegration/interfaces/active',
  MESHINTEGRATION_INTERFACES_VPN: '/meshintegration/interfaces/vpn',
  MESHINTEGRATION_INTERFACES_TAILSCALE: '/meshintegration/interfaces/tailscale',
  MESHINTEGRATION_INTERFACES_LOCAL: '/meshintegration/interfaces/local',
  MESHINTEGRATION_INTERFACE: '/meshintegration/interface',
  MESHINTEGRATION_INTERFACE_TYPE: '/meshintegration/interface/type',
  MESHINTEGRATION_INTERFACE_PRIORITY: '/meshintegration/interface/priority',
  MESHINTEGRATION_INTERFACE_ENABLE: '/meshintegration/interface/enable',
  MESHINTEGRATION_INTERFACE_DISABLE: '/meshintegration/interface/disable',
  MESHINTEGRATION_BEST: '/meshintegration/best',
  MESHINTEGRATION_BEST_VPN: '/meshintegration/best/vpn',
  MESHINTEGRATION_DISCOVERY_ADDRESSES: '/meshintegration/discovery/addresses',
  MESHINTEGRATION_BROADCAST_ADDRESSES: '/meshintegration/broadcast/addresses',
  MESHINTEGRATION_RESCAN: '/meshintegration/rescan',
  MESHINTEGRATION_DISCOVER: '/meshintegration/discover',
  MESHINTEGRATION_EVENTS: '/meshintegration/events',
  MESHINTEGRATION_TYPES: '/meshintegration/types',
  MESHINTEGRATION_PROVIDERS: '/meshintegration/providers',
  MESHINTEGRATION_EXPORT: '/meshintegration/export',
  MESHINTEGRATION_IMPORT: '/meshintegration/import',
  MESHINTEGRATION_STATS_RESET: '/meshintegration/stats/reset',

  // Mesh Local Data endpoints (Offline-First Kiosk Mode)
  // These endpoints query local mesh store instead of backend APIs
  MESH_LOCAL_ORDERS: '/mesh/local/orders',
  MESH_LOCAL_ORDER: '/mesh/local/orders/', // + {id}
  MESH_LOCAL_ORDERS_PENDING: '/mesh/local/orders/pending',
  MESH_LOCAL_PRODUCTS: '/mesh/local/products',
  MESH_LOCAL_PRODUCT: '/mesh/local/products/', // + {id}
  MESH_LOCAL_PRODUCTS_BY_SKU: '/mesh/local/products/by-sku',
  MESH_LOCAL_PRODUCTS_BY_BARCODE: '/mesh/local/products/by-barcode',
  MESH_LOCAL_CUSTOMERS: '/mesh/local/customers',
  MESH_LOCAL_CUSTOMER: '/mesh/local/customers/', // + {id}
  MESH_LOCAL_LOCATIONS: '/mesh/local/locations',
  MESH_LOCAL_LOCATION: '/mesh/local/locations/', // + {id}
  MESH_LOCAL_CONFIG: '/mesh/local/config',
  MESH_LOCAL_CONFIG_PRINTERS: '/mesh/local/config/printers',
  MESH_LOCAL_CONFIG_CAMERAS: '/mesh/local/config/cameras',
  MESH_LOCAL_CONFIG_SCANNERS: '/mesh/local/config/scanners',
  MESH_LOCAL_LABELS: '/mesh/local/labels',
  MESH_LOCAL_LABEL: '/mesh/local/labels/', // + {order_id}
  MESH_LOCAL_STATS: '/mesh/local/stats',
  MESH_LOCAL_QUEUE: '/mesh/local/queue',
  MESH_LOCAL_INVENTORY: '/mesh/local/inventory',

  // Operation tracking endpoints (workstation active tasks, history, summary)
  OPERATIONS_ACTIVE: '/operations/active',
  OPERATIONS_HISTORY: '/operations/history',
  OPERATIONS_SUMMARY: '/operations/summary',
  OPERATIONS_SYNC_TO_BACKEND: '/operations/sync-to-backend',

  // Mesh Label Sync endpoints (offline shipping label caching)
  MESH_LABELS_STATUS: '/mesh/labels/status',
  MESH_LABELS_STATS: '/mesh/labels/stats',
  MESH_LABELS_LIST: '/mesh/labels/list',
  MESH_LABELS_FORCE_SYNC: '/mesh/labels/force-sync',
  MESH_LABELS_DISK_SPACE: '/mesh/labels/disk-space',

  // Distributed Lock endpoints
  LOCKS_STATUS: '/locks/status',
  LOCKS_LIST: '/locks/list',
  LOCKS_ACTIVE: '/locks/active'
};

// Timeout configuration
export const API_TIMEOUT = 30000; // 30 secondes
export const API_UPLOAD_TIMEOUT = 120000; // 2 minutes for uploads

// Retry configuration
export const API_RETRY_ATTEMPTS = 3;
export const API_RETRY_DELAY = 1000; // 1 seconde

// JWT configuration
export const JWT_TOKEN_KEY = 'wms_access_token';
export const JWT_REFRESH_KEY = 'wms_refresh_token';
export const JWT_USER_KEY = 'wms_user_data';

export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest'
};

// CORS configuration
export const CORS_CONFIG = {
  credentials: 'include',
  mode: 'cors'
};

// Error messages are now localized via i18n
// Use t('errors.networkError') instead of ERROR_MESSAGES.NETWORK_ERROR
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'errors.networkError',
  TIMEOUT_ERROR: 'errors.timeoutError',
  UNAUTHORIZED: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  SERVER_ERROR: 'errors.serverError',
  VALIDATION_ERROR: 'errors.validationError'
};

// Status code configuration
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION_ERROR: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
};

// Pagination configuration
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  DEFAULT_PAGE: 1
};

// Cache configuration
export const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  USER_DATA_TTL: 30 * 60 * 1000, // 30 minutes
  STATIC_DATA_TTL: 60 * 60 * 1000 // 1 time
};

